import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Clock, User, ChevronRight } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogListPage = () => {
  const [articles, setArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    trackPageView('/blog', 'Blog');
    fetch(`${BACKEND_URL}/api/blog`)
      .then(res => res.json())
      .then(data => setArticles(data))
      .catch(err => console.error('Error fetching blog:', err));
  }, []);

  const categories = ['All', ...new Set(articles.map(a => a.category))];
  const filtered = activeCategory === 'All' ? articles : articles.filter(a => a.category === activeCategory);

  return (
    <div className="min-h-screen bg-void" data-testid="blog-page">
      <SEOHead
        title="Blog"
        description="Technical articles on cement grades, AAC blocks, GGBS, tile adhesives, sustainable construction, and India's construction industry trends. Expert insights from Avi Cement's technical team."
        path="/blog"
      />
      {/* Hero */}
      <section className="relative py-12 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.08) 0%, transparent 60%)' }}
        />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-coral font-mono text-sm mb-4 block">INSIGHTS</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Building<br /><span className="text-gradient">Knowledge</span>
            </h1>
            <p className="text-gray-400 text-lg">Expert insights on raw materials, construction techniques, and industry trends from the Avi Cement team.</p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 bg-surface sticky top-20 z-40 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-5 py-2 rounded-full font-display text-sm whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? 'bg-coral text-white'
                    : 'bg-surface-light text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {filtered.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <Link to={`/blog/${filtered[0].id}`} data-testid="featured-article" className="group block">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-surface border border-white/5 hover:border-white/15 rounded-2xl overflow-hidden transition-all">
                <div className="aspect-video lg:aspect-auto overflow-hidden">
                  <img
                    src={filtered[0].image}
                    alt={filtered[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="text-coral font-mono text-xs mb-3">{filtered[0].category}</span>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-coral transition-colors">
                    {filtered[0].title}
                  </h2>
                  <p className="text-gray-400 mb-6">{filtered[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-gray-500 text-sm">
                    <span className="flex items-center gap-1"><User className="w-4 h-4" /> {filtered[0].author}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {filtered[0].read_time}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section ref={ref} className="pb-20 md:pb-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(1).map((article, i) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/blog/${article.id}`}
                  data-testid={`blog-card-${article.id}`}
                  className="group block bg-surface border border-white/5 hover:border-white/20 rounded-2xl overflow-hidden transition-all h-full card-lift"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-mono text-coral">{article.category}</span>
                    <h3 className="font-display text-lg font-semibold text-white mt-2 mb-3 group-hover:text-coral transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_time}</span>
                      <span className="text-coral flex items-center gap-1 font-display">Read <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;
